import { type FC } from 'react';
import { createFragmentContainer, graphql } from 'react-relay/legacy';
import { FormattedMessage } from 'dibs-react-intl';
import { filterNulls } from 'dibs-ts-utils/exports/filterNulls';
import { filterFalsy } from 'dibs-ts-utils/exports/filterFalsy';

import { GridRow, GridCol } from 'dibs-elements/exports/Grid';
import { Spinner } from 'dibs-elements/exports/Spinner';

import { DesignerDirectoryFirmTile } from './DesignerDirectoryFirmTile';
import { DesignerDirectoryMatchmakerTile } from './DesignerDirectoryMatchmakerTile';
import { DesignerDirectoryError } from './DesignerDirectoryError';
import { loadingIntents, type LoadingIntent } from '../../shared';
import { Pagination } from '../../shared/Pagination';
import { DIRECTORY_PATH } from '../../constants';
import { getNumberOfPages } from '../helpers/paginationValues';

import styles from './styles/DesignerDirectoryContent.scss';

import { type DesignerDirectoryContent_viewer$data } from './__generated__/DesignerDirectoryContent_viewer.graphql';

type DesignerDirectoryContentProps = {
    error: boolean | null;
    handleContact: (arg: string | null) => void;
    handlePaginate: (arg: number) => void;
    loadingIntent: LoadingIntent | null;
    page: number;
    sortBy: string;
    showOnlyTpnFirms: boolean;
    showTestFirms: boolean;
    viewer: DesignerDirectoryContent_viewer$data;
    locationFilter: string | null;
};

const getPageHref = (pageNo: number): string =>
    pageNo === 1 ? DIRECTORY_PATH : `${DIRECTORY_PATH}?page=${pageNo}`;

const MATCHMAKER_TILE_POSITION = 2;

export const DesignerDirectoryContentComponent: FC<DesignerDirectoryContentProps> = ({
    error,
    handleContact,
    handlePaginate,
    loadingIntent,
    page,
    viewer,
    locationFilter,
}: DesignerDirectoryContentProps) => {
    if (error) {
        return <DesignerDirectoryError />;
    }

    const totalResults = viewer?.designFirms?.totalResults || 0;

    const numberOfPages = getNumberOfPages(totalResults);

    if (loadingIntent) {
        return (
            <>
                <div className={styles.loadingWrapper}>
                    <div className={styles.spinnerWrapper}>
                        <Spinner dark />
                    </div>
                </div>
                {loadingIntent === loadingIntents.PAGE_CHANGE && (
                    <Pagination
                        page={page}
                        href={getPageHref}
                        totalResults={totalResults}
                        numberOfPages={numberOfPages}
                        handlePaginate={handlePaginate}
                    />
                )}
            </>
        );
    }

    const firms = viewer?.designFirms?.edges || [];

    if (!firms.length && locationFilter) {
        const currentRegion = (viewer?.regions || [])
            .filter(filterNulls)
            .find(({ code }) => (code || '').toLowerCase() === locationFilter);
        return (
            <>
                <GridRow>
                    <GridCol desktop={12}>
                        <div className={styles.noResultsRow}>
                            {currentRegion?.regionName ? (
                                <FormattedMessage
                                    id="abt.designerDirectory.noResults"
                                    defaultMessage='We didn&apos;t find any results for "{location}".  Please select another location.'
                                    values={{
                                        location: currentRegion?.regionName,
                                    }}
                                />
                            ) : (
                                <FormattedMessage
                                    id="abt.designerDirectory.noResultsNoLocation"
                                    defaultMessage="We didn't find any results for that location.  Please select another location."
                                />
                            )}
                        </div>
                    </GridCol>
                </GridRow>
            </>
        );
    }

    //create array of tiles to display firms
    const firmTiles = firms
        .filter(filterNulls)
        .map(firm => firm?.node)
        .filter(filterFalsy)
        .map(node => (
            <DesignerDirectoryFirmTile
                key={node?.serviceId}
                firm={node}
                handleContact={handleContact}
            />
        ));

    if (page === 1) {
        //insert the matchmaker highlight tile so that it's displayed as the 3rd tile in the grid
        if (firmTiles.length > MATCHMAKER_TILE_POSITION) {
            firmTiles.splice(
                MATCHMAKER_TILE_POSITION,
                0,
                <DesignerDirectoryMatchmakerTile key="matchmaker-tile" />
            );
        } else {
            firmTiles.push(<DesignerDirectoryMatchmakerTile key="matchmaker-tile" />);
        }
    }

    return (
        <>
            <div className={styles.wrapper}>
                <GridRow>{firmTiles}</GridRow>
            </div>
            <GridRow>
                <GridCol desktop={12}>
                    <div className={styles.paginationRow}>
                        <Pagination
                            page={page}
                            href={getPageHref}
                            totalResults={totalResults}
                            numberOfPages={numberOfPages}
                            handlePaginate={handlePaginate}
                        />
                    </div>
                </GridCol>
            </GridRow>
        </>
    );
};

export const DesignerDirectoryContent = createFragmentContainer(DesignerDirectoryContentComponent, {
    viewer: graphql`
        fragment DesignerDirectoryContent_viewer on Viewer {
            designFirms(
                first: $resultsPerPage
                page: $page
                pageStartOffset: $pageStartOffset
                regionCodes: $regionCodes
                sortBy: $sortBy
                showOnlyTpnFirms: $showOnlyTpnFirms
                showTestFirms: $showTestFirms
            ) {
                totalResults
                edges {
                    node {
                        serviceId
                        ...DesignerDirectoryFirmTile_firm
                    }
                }
            }
            regions(regionType: TRADE_FIRM_REGION) {
                code
                regionName
            }
        }
    `,
});
